<!-- @catalog Single checkbox for a boolean. For a list of options prefer CInputSelect (multi) or CRadioGroup. -->
<template>
  <!-- @vue-expect-error Quasar QCheckbox types conflict with wrapper $attrs passthrough -->
  <q-checkbox
    ref="componentRef"
    v-bind="$attrs"
    :size="size"
    class="c-checkbox"
    :class="classes"
  >
    <div class="c-checkbox__content">
      <span v-if="label" class="c-checkbox__label-text">
        {{ label }}
      </span>

      <slot />

      <div v-if="hasMetaContent" class="c-checkbox__meta">
        <span v-if="required" class="c-checkbox__required">verplicht</span>

        <c-tooltip-standalone
          v-if="tooltip"
          :size="size"
          anchor="top middle"
          self="bottom middle"
        >
          {{ tooltip }}
        </c-tooltip-standalone>
      </div>
    </div>

    <c-tooltip-standalone
      v-if="disabledTooltip"
      wrapper
      class="c-checkbox__disabled-tooltip"
    >
      {{ disabledTooltip }}
    </c-tooltip-standalone>
  </q-checkbox>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from "vue";

const props = withDefaults(
  defineProps<{
    label?: string;
    tooltip?: string;
    disabledTooltip?: string;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    inline?: boolean;
    borderless?: boolean;
    required?: boolean;
  }>(),
  {
    label: "",
    tooltip: "",
    disabledTooltip: "",
    size: "xs",
    inline: false,
    borderless: false,
    required: false,
  },
);

const componentRef = useTemplateRef("componentRef");

const hasLabel = computed(() => props.label !== "");
const hasMetaContent = computed(() => props.tooltip || props.required);

const classes = computed(() => ({
  "c-checkbox--border": props.borderless === false,
  "c-checkbox--borderless": props.borderless,
  "c-checkbox--inline": props.inline,
  "c-checkbox--no-label": hasLabel.value === false,
  "c-checkbox--required": props.required,
  "c-checkbox--no-inline": props.inline === false,
}));

defineExpose({ componentRef });
</script>

<style lang="scss">
// Base checkbox styles
.c-checkbox {
  @apply tw-relative tw-flex tw-min-w-0 tw-select-none;

  // Quasar internal elements
  .q-checkbox__bg {
    @apply tw-rounded;
  }

  .q-checkbox__inner--truthy,
  .q-checkbox__inner--indet {
    @apply tw-text-green;
  }

  .q-checkbox__label {
    @apply tw-flex tw-min-w-0 tw-gap-x-[10px] tw-overflow-hidden;
  }

  // Custom content wrapper
  &__content {
    @apply tw-flex tw-min-w-0 tw-items-center;
  }

  &__label-text {
    @apply tw-min-w-0 tw-truncate tw-pl-1;
  }

  &__meta {
    @apply tw-flex tw-shrink-0 tw-items-center tw-gap-x-2 tw-self-center tw-pl-1;
  }

  &__required {
    @apply tw-text-[7px];
  }

  &__disabled-tooltip {
    @apply tw-absolute tw-inset-0 tw-z-10 tw-pl-1 tw-opacity-0;
  }
}

// Border variant (default)
.c-checkbox--border {
  @apply tw-h-10 tw-overflow-hidden tw-rounded-lg tw-border tw-border-gray-200 tw-pl-[6px] tw-transition;

  &:hover {
    @apply tw-border-primary;
  }

  // Padding for label content
  &:not(.c-checkbox--no-label) {
    padding-right: 12px;

    .q-checkbox__label {
      @apply tw-flex-1;
    }

    .c-checkbox__content {
      @apply tw-w-full tw-flex-1;
    }

    .c-checkbox__meta {
      @apply tw-ml-auto;
    }
  }

  // Reverse layout
  &.reverse:not(.c-checkbox--no-label) {
    padding-right: 0;
    padding-left: 12px;
  }
}

// Override Quasar's default inline behavior
.c-checkbox.c-checkbox--no-inline {
  @apply tw-flex;
}

// Borderless variant
.c-checkbox--borderless {
  @apply tw-h-auto tw-overflow-visible tw-rounded-none tw-pl-0;

  .q-checkbox__inner {
    @apply tw-p-0;
  }
}

// Inline variant
.c-checkbox--inline {
  @apply tw-inline-flex tw-max-w-full;

  .q-checkbox__label {
    @apply tw-min-w-0;
  }

  .c-checkbox__content {
    @apply tw-w-auto;
  }

  // Override border styles for inline
  &.c-checkbox--border:not(.c-checkbox--no-label) {
    .c-checkbox__content {
      @apply tw-flex-none;
    }

    .c-checkbox__meta {
      @apply tw-ml-0;
    }
  }
}

// No label variant
.c-checkbox--no-label {
  @apply tw-w-fit;

  &.c-checkbox--border {
    @apply tw-px-1;
  }
}

// Disabled state
.c-checkbox.disabled {
  pointer-events: auto !important;
  opacity: 1 !important;

  .q-checkbox__label {
    @apply tw-opacity-75;
  }

  .q-checkbox__bg.absolute {
    opacity: 0.5;
  }

  &.c-checkbox--border:hover {
    border-color: inherit !important;
  }
}

// Required label styling
.c-checkbox--required .q-checkbox__label {
  @apply tw-justify-between;
}

.c-checkbox--inline.c-checkbox--required .q-checkbox__label {
  @apply tw-justify-start;
}
</style>
